import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FileSignature, Plus } from 'lucide-react';
import { api } from '../lib/api';
import { usd, StatusChip, ErrorBox, btnCls, btn2Cls } from '../lib/q2c';
import { easternDay } from '../../modules/q2c/eastern-day.mjs';

// The Copilot's "ready for approval" mark on a draft (schema-d); any edit clears it. The approver
// line is information only: the portal records one partner's Approve tap.
function ReadyBadge({ q }) {
  if (q.status !== 'draft' || !q.ready_for_approval_at) return null;
  const by = String(q.ready_for_approval_by || '').split('@')[0];
  return (
    <span className="block text-[11px] text-[var(--text-secondary)]" title={q.approvers?.rule || ''}>
      <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">Ready for approval</span>
      {' '}{by ? `marked by ${by} ` : 'marked '}{easternDay(q.ready_for_approval_at)}
      {q.approvers ? ` · ${q.approvers.partners > 1 ? 'two partners approve (over $25K)' : 'one partner approves'}` : ''}
    </span>
  );
}

const FILTERS = [['open', 'Open'], ['accepted', 'Accepted'], ['all', 'All']];

export default function QuotesPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  // Show the empty state only after the current list request succeeds.
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState('open');
  const [latestOnly, setLatestOnly] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  // A failed load empties the list so the last filter's rows and total never show under the new filter.
  const load = useCallback(async () => {
    const mine = ++seq.current; // only the latest filter's response may update the list
    try { const r = await api.get(`/quotes?status=${status}`); if (mine === seq.current) { setRows(r.quotes || []); setError(''); setLoaded(true); } } catch (e) { if (mine === seq.current) { setRows([]); setError(e.message); setLoaded(false); } }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  async function create(kind) {
    setBusy(true);
    try {
      const r = await api.post('/quotes', { kind, title: kind === 'proposal' ? 'New proposal' : 'New quote' });
      navigate(`/quotes/${r.quote.id}`);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const shown = latestOnly ? rows.filter((r) => r.is_latest) : rows;
  const openValue = shown.filter((r) => ['sent', 'viewed'].includes(r.display_status)).reduce((s, r) => s + Number(r.total || 0), 0);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--text-primary)]"><FileSignature size={20} /> Quotes & proposals</h1>
          {/* Show the count and total only after a good load. */}
          {loaded && (
            <p className="text-sm text-[var(--text-secondary)]">{shown.length} shown · {usd(openValue)} out with customers</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-[var(--border)]">
            {FILTERS.map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1.5 text-xs font-medium ${status === k ? 'bg-primary text-white' : 'bg-white text-[var(--text-secondary)] dark:bg-slate-800'}`}>{l}</button>
            ))}
          </div>
          <label className="flex items-center gap-1 text-xs text-[var(--text-secondary)]"><input type="checkbox" checked={latestOnly} onChange={(e) => setLatestOnly(e.target.checked)} /> latest versions</label>
          <button className={btn2Cls} disabled={busy} onClick={() => create('proposal')}><Plus size={13} className="mr-1 inline" />Proposal</button>
          <button className={btnCls} disabled={busy} onClick={() => create('quote')}><Plus size={14} className="mr-1 inline" />Quote</button>
        </div>
      </div>
      <ErrorBox msg={error} />
      {/* Phones: one card per quote; the table is for wider screens. */}
      <div className="space-y-2 sm:hidden">
        {shown.map((q) => (
          <Link key={q.id} to={`/quotes/${q.id}`} className="block rounded-xl border border-[var(--border)] bg-white p-3 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="break-words font-medium text-[var(--text-primary)]">{q.title}</p>
                <p className="break-words text-xs text-[var(--text-secondary)]"><span className="font-mono">{q.number} V{q.version}</span>{q.kind === 'proposal' ? ' · proposal' : ''} · {q.customer_name || 'no customer yet'}</p>
              </div>
              <div className="shrink-0 text-right"><p className="font-semibold">{usd(q.total)}</p><StatusChip status={q.display_status} /></div>
            </div>
            <p className="mt-1 text-xs text-[var(--text-secondary)]">{q.margin_pct != null ? `Margin ${q.margin_pct}%` : 'Margin unknown'} · {(q.owner_email || '').split('@')[0] || 'no owner'}</p>
            <div className="mt-1"><ReadyBadge q={q} /></div>
          </Link>
        ))}
        {loaded && !shown.length && <p className="rounded-xl border border-dashed border-[var(--border)] p-6 text-center text-sm text-[var(--text-secondary)]">No quotes here yet.</p>}
      </div>
      <div className="hidden overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900 sm:block">
        <table className="w-full min-w-[680px] text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wide text-[var(--text-secondary)]">
            <th className="px-3 py-2">No.</th><th className="px-3 py-2">Title</th><th className="px-3 py-2">Customer</th>
            <th className="px-3 py-2 text-right">Total</th><th className="px-3 py-2 text-right">Margin</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Owner</th>
          </tr></thead>
          <tbody>
            {shown.map((q) => (
              <tr key={q.id} className="border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800">
                <td className="px-3 py-2 font-mono text-xs"><Link to={`/quotes/${q.id}`} className="text-primary">{q.number} V{q.version}</Link>{q.kind === 'proposal' && <span className="ml-1 text-[10px] text-[var(--text-secondary)]">proposal</span>}</td>
                <td className="px-3 py-2"><Link to={`/quotes/${q.id}`}>{q.title}</Link></td>
                <td className="px-3 py-2 text-[var(--text-secondary)]">{q.customer_name || '—'}</td>
                <td className="px-3 py-2 text-right font-medium">{usd(q.total)}</td>
                <td className="px-3 py-2 text-right text-xs">{q.margin_pct != null ? `${q.margin_pct}%` : '—'}</td>
                <td className="px-3 py-2"><StatusChip status={q.display_status} /><div className="mt-1"><ReadyBadge q={q} /></div></td>
                <td className="px-3 py-2 text-xs text-[var(--text-secondary)]">{(q.owner_email || '').split('@')[0]}</td>
              </tr>
            ))}
            {loaded && !shown.length && <tr><td colSpan={7} className="p-6 text-center text-[var(--text-secondary)]">No quotes here yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
